import { ConjunctionMode } from "@redotech/congo-enums/conjunction-mode";
import {
  CustomerTagOperation,
  FrequencyCapMode,
  SendWebhookStepAuthType,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import {
  type InsertableAutomationStepPayloadKey,
  type PublicTypedAutomationStepPayloadKey,
} from "@redotech/redo-flows-common/automation-step-catalog";
import { publicSkipActionTypeValues } from "@redotech/redo-flows-common/public-condition-expression";
import { ShopperAgentMessageChannelKinds } from "@redotech/redo-flows-common/shopper-agent-message-channel";
import { WaitTimeUnit } from "@redotech/redo-flows-common/steps";
import * as z from "zod";

const waitTimeUnit = z.enum(WaitTimeUnit);
const tagOperation = z.enum(CustomerTagOperation);

const frequencyCap = z.union([
  z.object({ mode: z.literal(FrequencyCapMode.NO_REENTRY) }),
  z.object({
    mode: z.literal(FrequencyCapMode.COOLDOWN),
    value: z.number(),
    unit: waitTimeUnit,
  }),
]);

const conditionConjunction = z.enum(ConjunctionMode);

const publicSkipActionType = z.enum(publicSkipActionTypeValues);

const marketingAutomationSkipConditionPatch = z.object({
  expression: z.lazy(() => marketingAutomationConditionExpressionPatch),
  actionTypes: z.array(publicSkipActionType).nullish(),
});

const marketingAutomationEntryConditionPatch = z.object({
  expression: z.lazy(() => marketingAutomationConditionExpressionPatch),
});

export const marketingAutomationTriggerPatch = z.object({
  customTitle: z.string().nullish(),
  nextId: z.string().nullish(),
  shouldSkipSmartSending: z.boolean().nullish(),
  frequencyCap: frequencyCap.nullish(),
  triggerSpecificFields: z.unknown().nullish(),
  skipConditions: z
    .object({
      conjunction: conditionConjunction,
      conditions: z.array(marketingAutomationSkipConditionPatch),
    })
    .nullish(),
  entryConditions: z
    .object({
      conjunction: conditionConjunction,
      conditions: z.array(marketingAutomationEntryConditionPatch),
    })
    .nullish(),
});

export const marketingAutomationWaitPatch = z.object({
  customTitle: z.string().nullish(),
  numDays: z.number().nullish(),
  numSeconds: z.number().nullish(),
  timeUnit: waitTimeUnit.nullish(),
  nextId: z.string().nullish(),
});

export const marketingAutomationWaitForEventPatch = z.object({
  customTitle: z.string().nullish(),
  eventType: z.string().nullish(),
  nextId: z.string().nullish(),
  filter: z
    .object({
      producer: z.array(z.string()).nullish(),
      emailIdentityResolver: z.array(z.string()).nullish(),
      phoneIdentityResolver: z.array(z.string()).nullish(),
    })
    .nullish(),
});

export const marketingAutomationSendEmailPatch = z.object({
  customTitle: z.string().nullish(),
  templateId: z.string().nullish(),
  originTemplateId: z.string().nullish(),
  emailAddressFieldName: z.string().nullish(),
  recipientNameFieldName: z.string().nullish(),
  nextId: z.string().nullish(),
  disabled: z.boolean().nullish(),
});

export const marketingAutomationSendSmsPatch = z.object({
  customTitle: z.string().nullish(),
  templateId: z.string().nullish(),
  phoneNumberFieldName: z.string().nullish(),
  recipientNameFieldName: z.string().nullish(),
  nextId: z.string().nullish(),
  disabled: z.boolean().nullish(),
});

export const marketingAutomationSendWebhookPatch = z.object({
  customTitle: z.string().nullish(),
  destinationUrl: z.string().nullish(),
  headers: z.array(z.object({ key: z.string(), value: z.string() })).nullish(),
  payload: z.string().nullish(),
  nextId: z.string().nullish(),
  disabled: z.boolean().nullish(),
  authType: z.enum(SendWebhookStepAuthType).nullish(),
  oauth2Config: z
    .object({
      tokenUrl: z.string().nullish(),
      clientId: z.string().nullish(),
      clientSecret: z.string().nullish(),
      audience: z.string().nullish(),
      scope: z.string().nullish(),
    })
    .nullish(),
});

export const marketingAutomationStartAgentConversationPatch = z.object({
  customTitle: z.string().nullish(),
  channel: z
    .object({
      kind: z.enum(ShopperAgentMessageChannelKinds),
      subscribers: z.boolean().nullish(),
      extendedOutreach: z.boolean().nullish(),
    })
    .nullish(),
  strategyId: z.string().nullish(),
  nextId: z.string().nullish(),
  disabled: z.boolean().nullish(),
});

export const marketingAutomationManageCustomerTagsPatch = z.object({
  customTitle: z.string().nullish(),
  operation: tagOperation.nullish(),
  tagIds: z.array(z.string()).nullish(),
  nextId: z.string().nullish(),
  disabled: z.boolean().nullish(),
});

export const marketingAutomationManageStaticSegmentPatch = z.object({
  customTitle: z.string().nullish(),
  operation: tagOperation.nullish(),
  segmentId: z.string().nullish(),
  nextId: z.string().nullish(),
  disabled: z.boolean().nullish(),
});

export const marketingAutomationEndPatch = z.object({
  customTitle: z.string().nullish(),
});

export const marketingAutomationAbTestPatch = z.object({
  customTitle: z.string().nullish(),
  name: z.string().nullish(),
  description: z.string().nullish(),
  variants: z
    .array(
      z.object({
        id: z.string().nullish(),
        name: z.string().nullish(),
        weight: z.number().nullish(),
        nextId: z.string().nullish(),
      }),
    )
    .nullish(),
});

export const marketingAutomationConditionExpressionPatch = z.object({
  existingSegment: z.object({ segmentIds: z.array(z.string()) }).nullish(),
  inlineSegment: z.object({ conditions: z.unknown() }).nullish(),
  triggerData: z.object({ predicate: z.unknown() }).nullish(),
});

export const marketingAutomationConditionPatch = z.object({
  customTitle: z.string().nullish(),
  description: z.string().nullish(),
  naturalLanguageSummary: z.string().nullish(),
  expression: marketingAutomationConditionExpressionPatch.nullish(),
  nextTrueId: z.string().nullish(),
  nextFalseId: z.string().nullish(),
});

const stepPayloads = {
  trigger: marketingAutomationTriggerPatch.nullish(),
  wait: marketingAutomationWaitPatch.nullish(),
  waitForEvent: marketingAutomationWaitForEventPatch.nullish(),
  sendEmail: marketingAutomationSendEmailPatch.nullish(),
  sendSms: marketingAutomationSendSmsPatch.nullish(),
  sendWebhook: marketingAutomationSendWebhookPatch.nullish(),
  manageCustomerTags: marketingAutomationManageCustomerTagsPatch.nullish(),
  manageStaticSegment: marketingAutomationManageStaticSegmentPatch.nullish(),
  end: marketingAutomationEndPatch.nullish(),
  abTest: marketingAutomationAbTestPatch.nullish(),
  condition: marketingAutomationConditionPatch.nullish(),
  startAgentConversation:
    marketingAutomationStartAgentConversationPatch.nullish(),
} satisfies Record<PublicTypedAutomationStepPayloadKey, z.ZodTypeAny>;

export const marketingAutomationStepUpdateOp = z.object({
  id: z.string(),
  ...stepPayloads,
});

const insertStepPayloads = {
  wait: marketingAutomationWaitPatch.nullish(),
  waitForEvent: marketingAutomationWaitForEventPatch.nullish(),
  sendEmail: marketingAutomationSendEmailPatch.nullish(),
  sendSms: marketingAutomationSendSmsPatch.nullish(),
  sendWebhook: marketingAutomationSendWebhookPatch.nullish(),
  manageCustomerTags: marketingAutomationManageCustomerTagsPatch.nullish(),
  manageStaticSegment: marketingAutomationManageStaticSegmentPatch.nullish(),
  end: marketingAutomationEndPatch.nullish(),
  abTest: marketingAutomationAbTestPatch.nullish(),
  condition: marketingAutomationConditionPatch.nullish(),
  startAgentConversation:
    marketingAutomationStartAgentConversationPatch.nullish(),
} satisfies Record<InsertableAutomationStepPayloadKey, z.ZodTypeAny>;

export const marketingAutomationStepInsertOp = z.object({
  after: z.string().nullish(),
  before: z.string().nullish(),
  branch: z.enum(["true", "false"]).nullish(),
  variantId: z.string().nullish(),
  ...insertStepPayloads,
});

export const marketingAutomationStepRemoveOp = z.object({ id: z.string() });

export const marketingAutomationStepMoveOp = z.object({
  id: z.string(),
  after: z.string().nullish(),
  before: z.string().nullish(),
  branch: z.enum(["true", "false"]).nullish(),
  variantId: z.string().nullish(),
});

export const marketingAutomationStepOperation = z.object({
  update: marketingAutomationStepUpdateOp.nullish(),
  insert: marketingAutomationStepInsertOp.nullish(),
  remove: marketingAutomationStepRemoveOp.nullish(),
  move: marketingAutomationStepMoveOp.nullish(),
});

export type MarketingAutomationStepOperation = z.infer<
  typeof marketingAutomationStepOperation
>;
